import { StoreApi, useStore } from "zustand";

/**
 * Adds custom selectors that allow getting store values as hooks.
 * For example
 * const { useStoreState } = usePlayerStore();
 * const isPaused = useStoreState.isPaused();
 * Read more https://docs.pmnd.rs/zustand/guides/auto-generating-selectors
 */

type UseStoreState<P> = { [K in keyof P]: () => P[K] };

type StoreProperties<S extends { getState: () => unknown }> = ReturnType<
  S["getState"]
>;

export type WithSelectors<S, P> = S extends { getState: () => P }
  ? S & { useStoreState: UseStoreState<P> }
  : never;

export const createSelectors = <
  S extends StoreApi<object>,
  P extends {
    [K in keyof StoreProperties<S>]: StoreProperties<S>[K];
  }
>(
  _store: S
): WithSelectors<S, P> => {
  const store = _store as WithSelectors<typeof _store, P>;
  store.useStoreState = {} as UseStoreState<P>;
  for (const k of Object.keys(store.getState())) {
    store.useStoreState[k] = () =>
      // eslint-disable-next-line
      useStore(_store, s => s[k as keyof typeof s]);
  }

  return store;
};
